import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { QuoteCTA } from './QuoteCTA';
interface Feature {
  title: string;
  description: string;
  icon: React.ElementType;
}
interface FAQ {
  question: string;
  answer: string;
}
interface PageTemplateProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  heroImage: string;
  overviewTitle: string;
  overviewText: string[];
  featuresTitle: string;
  features: Feature[];
  faqs?: FAQ[];
}
export function PageTemplate({
  eyebrow,
  title,
  subtitle,
  heroImage,
  overviewTitle,
  overviewText,
  featuresTitle,
  features,
  faqs
}: PageTemplateProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="bg-surface pb-20" data-id="element-246">
      {/* Hero */}
      <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-navy text-white" data-id="element-247">
        <div className="absolute inset-0 z-0" data-id="element-248">
          <img src={heroImage} alt={title} className="w-full h-full object-cover opacity-20 mix-blend-overlay" data-id="element-249" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" data-id="element-250" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-251">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-252">
            <span className="inline-block text-sm font-bold uppercase tracking-wider text-accent mb-4" data-id="element-253">
              {eyebrow}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-254">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-255">
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-256">
              <button className="bg-accent hover:bg-accent-hover text-navy-dark font-bold py-3.5 px-8 rounded-full transition-all transform hover:-translate-y-0.5 shadow-lg" data-id="element-257">
                Get a Free Quote
              </button>
              <a href="tel:+17147264999" className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-semibold py-3.5 px-8 rounded-full transition-colors flex items-center justify-center backdrop-blur-sm" data-id="element-258">
                Call (714) 726-4999
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white" data-id="element-259">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-260">
          <div className="max-w-3xl" data-id="element-261">
            <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-262">
              {overviewTitle}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed" data-id="element-263">
              {overviewText.map((paragraph, idx) => <p key={idx} data-id="element-264">{paragraph}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-surface" data-id="element-265">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-266">
          <h2 className="text-3xl font-bold text-navy mb-12 text-center" data-id="element-267">
            {featuresTitle}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-268">
            {features.map((feature, idx) => {
            const Icon = feature.icon;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-269">
                  <div className="w-12 h-12 bg-navy/5 rounded-xl flex items-center justify-center mb-6" data-id="element-270">
                    <Icon className="w-6 h-6 text-navy" data-id="element-271" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-3" data-id="element-272">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-273">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* FAQs (Optional) */}
      {faqs && faqs.length > 0 && <section className="py-20 bg-white border-t border-slate-100" data-id="element-274">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-275">
            <h2 className="text-3xl font-bold text-navy mb-10 text-center" data-id="element-276">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-277">
              {faqs.map((faq, idx) => <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-278">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full px-6 py-4 text-left bg-slate-50 hover:bg-slate-100 transition-colors flex justify-between items-center" data-id="element-279">
                    <span className="font-bold text-navy" data-id="element-280">{faq.question}</span>
                    <span className={`transform transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-281">
                      ↓
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-6 py-4 bg-white text-slate-600" data-id="element-282">
                      {faq.answer}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* Final CTA */}
      <QuoteCTA data-id="element-283" />
    </div>;
}